// Analytics dashboard — org-level KPI summary tiles. Phase 2.

import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";

interface AnalyticsSummary {
  total_patients:       number;
  active_srs:           number;
  completed_srs_30d:    number;
  avg_csat_30d:         number | null;
  high_risk_patients:   number;
  escalations_30d:      number;
  compliance_rate_30d:  number | null;
  revenue_paise_30d:    number;
}

function useAnalytics(): ReturnType<typeof useQuery<AnalyticsSummary>> {
  return useQuery<AnalyticsSummary>({
    queryKey: ["analytics-summary"],
    queryFn:  () => apiFetch<AnalyticsSummary>("/analytics/summary"),
    refetchInterval: 60_000,
  });
}

const CLS_NEUTRAL = "text-slate-500";
const CLS_RED     = "text-red-600";
const CLS_GREEN   = "text-green-600";

function formatInr(paise: number): string {
  const rupees = paise / 100;
  return rupees.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
}

function csatAccent(val: number | null): string {
  if (val === null) return CLS_NEUTRAL;
  if (val < 3)  return CLS_RED;
  if (val <= 4) return "text-amber-600";
  return CLS_GREEN;
}

function complianceAccent(rate: number | null): string {
  if (rate === null) return CLS_NEUTRAL;
  if (rate > 0.9) return CLS_GREEN;
  if (rate > 0.7) return "text-amber-600";
  return CLS_RED;
}

interface TileProps {
  label:  string;
  value:  string | number;
  accent?: string;
}

function StatTile({ label, value, accent }: TileProps): JSX.Element {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${accent ?? CLS_NEUTRAL}`}>{value}</p>
    </div>
  );
}

function AnalyticsTiles({ d }: { d: AnalyticsSummary }): JSX.Element {
  const csatDisplay = d.avg_csat_30d !== null ? d.avg_csat_30d.toFixed(1) : "—";
  const complianceDisplay = d.compliance_rate_30d !== null
    ? `${(d.compliance_rate_30d * 100).toFixed(1)}%`
    : "—";

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatTile label="Total Patients"       value={d.total_patients}     accent="text-navy-800" />
      <StatTile label="Active SRs"           value={d.active_srs} />
      <StatTile
        label="Completed (30d)"
        value={d.completed_srs_30d}
        accent={d.completed_srs_30d > 0 ? CLS_GREEN : undefined}
      />
      <StatTile
        label="Avg CSAT"
        value={csatDisplay}
        accent={csatAccent(d.avg_csat_30d)}
      />
      <StatTile
        label="High Risk Patients"
        value={d.high_risk_patients}
        accent={d.high_risk_patients > 0 ? CLS_RED : "text-slate-500"}
      />
      <StatTile
        label="Escalations (30d)"
        value={d.escalations_30d}
        accent={d.escalations_30d > 0 ? CLS_RED : undefined}
      />
      <StatTile
        label="Compliance Rate (30d)"
        value={complianceDisplay}
        accent={complianceAccent(d.compliance_rate_30d)}
      />
      <StatTile
        label="Revenue (30d)"
        value={formatInr(d.revenue_paise_30d)}
      />
    </div>
  );
}

export function AnalyticsPage(): JSX.Element {
  const { data, isLoading, isError } = useAnalytics();

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <h1 className="text-xl font-semibold text-slate-900">Analytics</h1>

        {isLoading && (
          <p className="py-8 text-center text-sm text-slate-400" aria-busy="true">Loading…</p>
        )}

        {isError && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-xl px-6 py-4">
            <p className="text-sm text-red-700 font-medium">Failed to load analytics data.</p>
            <p className="text-xs text-red-500 mt-1">Check your connection or try refreshing the page.</p>
          </div>
        )}

        {!isLoading && !isError && data && <AnalyticsTiles d={data} />}

        {!isLoading && !isError && (
          <p className="text-xs text-slate-400 text-center">
            Data reflects last 30 days for your organisation.
          </p>
        )}
      </div>
    </Shell>
  );
}
